import { useState, useEffect, useCallback, useMemo, FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import { publicApi } from '@/services/publicApi';
import type { PublicListing } from '@/services/publicApi';
import { useApiData } from '@/hooks/useApiData';
import { useSettings } from '@/context/SettingsContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { buildWhatsAppLink } from '@/lib/utils';
import { isValidEmail, isValidPhone } from '@/lib/validation';
import ImageWithFallback from '@/components/base/ImageWithFallback';
import Lightbox from './components/Lightbox';
import SimilarListings from './components/SimilarListings';

const UPLOADS_BASE = (() => {
  const uploadBase = import.meta.env.VITE_UPLOAD_BASE_URL;
  const apiBase = import.meta.env.VITE_PUBLIC_API_URL;
  const base = uploadBase || apiBase || '';
  return base.endsWith('/') ? base.slice(0, -1) : base;
})();

const STATUS_LABELS: Record<string, string> = {
  available: 'Available',
  under_review: 'Under Review',
  leased: 'Leased',
  sold: 'Sold',
  off_market: 'Off Market',
};

const CATEGORY_LABELS: Record<string, string> = {
  commercial: 'Commercial',
  industrial: 'Industrial',
  agricultural: 'Agricultural',
  residential: 'Residential',
  mixed_use: 'Mixed Use',
};

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-teal-500/15 text-teal-400 border-teal-500/25',
  under_review: 'bg-accent-100 text-accent-700 border-accent-500/25',
  leased: 'bg-primary-500/15 text-primary-700 border-primary-500/25',
  sold: 'bg-rose-500/15 text-rose-400 border-rose-500/25',
  off_market: 'bg-foreground-500/15 text-foreground-400 border-foreground-500/25',
};

const PURPOSE_OPTIONS = [
  'Agriculture / Farming',
  'Commercial / Retail',
  'Industrial / Manufacturing',
  'Warehouse / Logistics',
  'Residential / Housing',
  'Solar / Renewable Energy',
  'Data Center / IT',
  'Events / Hospitality',
  'Parking / Storage',
  'Other',
];

function buildImageUrls(paths: string[] | null): string[] {
  if (!paths || !Array.isArray(paths)) return [];
  return paths
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .map((p) => {
      if (p.startsWith('data:')) return p;
      if (p.startsWith('http://') || p.startsWith('https://')) return p;
      if (p.startsWith('/uploads/')) return `${UPLOADS_BASE}${p}`;
      if (p.startsWith('uploads/')) return `${UPLOADS_BASE}/${p}`;
      return '';
    })
    .filter((p) => p.length > 0);
}

function formatRent(rent: number, _currency: string, period: string): string {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });
  return `${formatter.format(rent)}/${period || 'mo'}`;
}

function formatSize(acres: number): string {
  if (acres >= 1) return `${acres} ${acres === 1 ? 'Acre' : 'Acres'}`;
  return `${(acres * 43560).toFixed(0)} Sq Ft`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function DetailSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-[420px] bg-background-200/50 rounded-2xl mb-6" />
      <div className="space-y-4 max-w-3xl mx-auto">
        <div className="h-6 bg-background-200/50 rounded w-1/3" />
        <div className="h-10 bg-background-200/50 rounded w-3/4" />
        <div className="h-4 bg-background-200/50 rounded w-1/2" />
      </div>
    </div>
  );
}

function EnquiryForm({ listing }: { listing: PublicListing }) {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    purpose: '',
    description: '',
    contact_alt: '',
  });
  const [descCount, setDescCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState((f) => ({ ...f, [name]: value }));
    if (name === 'description') setDescCount(value.length);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formState.contact_alt.trim()) {
      setStatus('success');
      return;
    }
    if (!formState.name.trim()) {
      setErrorMsg('Your name is required.');
      setStatus('error');
      return;
    }
    if (!formState.phone.trim()) {
      setErrorMsg('Mobile number is required.');
      setStatus('error');
      return;
    }
    if (!isValidPhone(formState.phone)) {
      setErrorMsg('Please enter a valid mobile number (7-15 digits, no letters or @).');
      setStatus('error');
      return;
    }
    if (formState.email && !isValidEmail(formState.email)) {
      setErrorMsg('Please enter a valid email address.');
      setStatus('error');
      return;
    }
    setSubmitting(true);
    setStatus('idle');
    setErrorMsg('');
    try {
      await publicApi.submitPropertyEnquiry({
        name: formState.name,
        email: formState.email || undefined,
        phone: formState.phone,
        purpose: formState.purpose || undefined,
        description: formState.description || undefined,
        listing_id: listing.id,
        listing_title: listing.title,
        budget: listing.expected_rent > 0
          ? `₹${listing.expected_rent.toLocaleString('en-IN')}/${listing.rent_period || 'mo'}`
          : undefined,
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-teal-500/10 border border-teal-500/30 rounded-2xl p-6 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-500/20 mx-auto mb-3">
          <i className="ri-check-line text-teal-400 text-xl" />
        </div>
        <h4 className="font-semibold text-foreground-950 mb-1">Enquiry Submitted!</h4>
        <p className="text-sm text-foreground-500 mb-4">
          Your enquiry has been saved. Our team will contact you within 24 hours.
        </p>
        <button
          onClick={() => { setStatus('idle'); setFormState({ name: '', email: '', phone: '', purpose: '', description: '', contact_alt: '' }); setDescCount(0); }}
          className="text-xs text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form>
      {/* Honeypot */}
      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        value={formState.contact_alt}
        onChange={handleChange}
        className="absolute opacity-0 w-0 h-0"
      />
      <div>
        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
          Full Name <span className="text-rose-400">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formState.name}
          onChange={handleChange}
          placeholder="Your full name"
          required
          className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/60 transition-colors"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
            Mobile Number <span className="text-rose-400">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={formState.phone}
            onChange={handleChange}
            placeholder="+91 XXXXX XXXXX"
            required
            className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/60 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Email</label>
          <input
            type="email"
            name="email"
            value={formState.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/60 transition-colors"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Purpose / Requirement</label>
        <select
          name="purpose"
          value={formState.purpose}
          onChange={handleChange}
          className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-primary-500/60 transition-colors cursor-pointer"
        >
          <option value="">Select your purpose</option>
          {PURPOSE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Short Description</label>
        <textarea
          name="description"
          value={formState.description}
          onChange={handleChange}
          rows={3}
          maxLength={500}
          placeholder="Tell us more about your requirement, timeline, or any specific needs..."
          className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/60 transition-colors resize-none"
        />
        <p className="text-right text-xs text-foreground-400 mt-1">{descCount}/500</p>
      </div>
      {listing.expected_rent > 0 && (
        <div className="bg-primary-500/8 border border-primary-700/20 rounded-xl p-3 flex items-center gap-2 text-sm text-primary-700">
          <i className="ri-money-rupee-circle-line shrink-0" />
          <span>Budget will be set to <strong>{formatRent(listing.expected_rent, listing.rent_currency, listing.rent_period)}</strong> based on this property</span>
        </div>
      )}
      {status === 'error' && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 flex items-start gap-2 text-sm text-rose-500">
          <i className="ri-error-warning-line shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
      >
        <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}`} />
        {submitting ? 'Submitting...' : 'Submit Enquiry'}
      </button>
      <p className="text-xs text-foreground-400 text-center">
        Your enquiry will be saved as a lead with <strong>Hot priority</strong> and our team will contact you within 24 hours.
      </p>
    </form>
  );
}

export default function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [listing, setListing] = useState<PublicListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { data: settingsResponse } = useApiData(
    () => publicApi.getSettings(),
    []
  );

  const { settings } = useSettings();
  const whatsappLink = buildWhatsAppLink(settings.general.whatsapp_number);
  const whatsappDisplay = settings.general.whatsapp_number || '+91 90350 63455';

  const showPrices = useMemo(() => {
    const features = settingsResponse?.data?.features as Record<string, unknown> | undefined;
    return features?.show_prices !== false;
  }, [settingsResponse]);

  const showOwnerInfo = useMemo(() => {
    return listing && (listing as unknown as Record<string, unknown>).show_owner_info === 1;
  }, [listing]);

  const fetchListing = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await publicApi.getListing(parseInt(id, 10));
      setListing(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load listing');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchListing();
    window.scrollTo(0, 0);
  }, [fetchListing]);

  const images = listing ? buildImageUrls(listing.images_json) : [];
  const hasMultipleImages = images.length > 1;

  const goNext = useCallback(() => {
    if (images.length === 0) return;
    setActiveImage((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    if (images.length === 0) return;
    setActiveImage((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, goNext, goPrev]);

  usePageMeta({
    title: listing
      ? `${listing.title} — ${listing.location || listing.city || 'Karnataka'} Land | CashMyProperty.ai`
      : 'Land Listing Details | CashMyProperty.ai',
    description: listing
      ? `${listing.title} in ${listing.city || listing.location || 'Karnataka'}. ${formatSize(listing.size_acres)} ${CATEGORY_LABELS[listing.category] || listing.category} land. Enquire now for site visits and negotiations.`
      : 'View verified land listing details, expected rent, size and location on CashMyProperty.ai.',
    canonicalUrl: listing
      ? `https://cashmyproperty.ai/listings/${listing.id}`
      : undefined,
    type: 'product',
    ogImage: images[0] || undefined,
  });

  return (
    <PageLayout>
      {loading ? (
        <div className="container mx-auto px-4 lg:px-8 py-16">
          <DetailSkeleton />
        </div>
      ) : error || !listing ? (
        <div className="container mx-auto px-4 lg:px-8 py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-rose-500/10 mx-auto mb-5">
            <i className="ri-error-warning-line text-2xl text-rose-500" />
          </div>
          <h2 className="text-2xl font-display font-bold text-foreground-950 mb-2">
            {error ? 'Failed to Load Listing' : 'Listing Not Found'}
          </h2>
          <p className="text-foreground-500 mb-6 max-w-md mx-auto">
            {error || 'This listing may have been removed or is no longer available.'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={fetchListing}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" /> Try Again
            </button>
            <Link
              to="/listings"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-700/50 text-primary-400 font-medium hover:bg-primary-500/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" /> Back to Listings
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Breadcrumb */}
          <div className="container mx-auto px-4 lg:px-8 pt-6 pb-2">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <Link to="/listings" className="hover:text-primary-500 transition-colors">Listings</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-foreground-700 truncate max-w-[240px]">{listing.title}</span>
            </nav>
          </div>

          {/* Main content grid */}
          <section className="container mx-auto px-4 lg:px-8 py-6">
            <div className="grid lg:grid-cols-3 gap-8">

              {/* Left: Images + Details */}
              <div className="lg:col-span-2 space-y-6">

                {/* Image Gallery */}
                {images.length > 0 ? (
                  <div className="rounded-2xl overflow-hidden bg-background-100 border border-background-200/60">
                    <div
                      className="relative w-full h-[320px] md:h-[460px] bg-background-200/30 cursor-zoom-in group/main-img"
                      onClick={() => setLightboxOpen(true)}
                      role="button"
                      tabIndex={0}
                      aria-label="View fullscreen gallery"
                      onKeyDown={(e) => { if (e.key === 'Enter') setLightboxOpen(true); }}
                    >
                      <ImageWithFallback
                        src={images[activeImage]}
                        alt={`${listing.title} — Photo ${activeImage + 1}`}
                        className="w-full h-full object-cover object-top transition-opacity duration-300"
                        fallbackClassName="w-full h-full"
                      />
                      {/* Badges */}
                      <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-background-950/60 backdrop-blur-sm text-background-50">
                          {CATEGORY_LABELS[listing.category] || listing.category}
                        </span>
                        {listing.is_verified === 1 && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-teal-500 text-white">
                            <i className="ri-shield-check-line" /> Verified
                          </span>
                        )}
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[listing.status] || STATUS_COLORS.available}`}>
                          {STATUS_LABELS[listing.status] || listing.status}
                        </span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/main-img:opacity-100 transition-opacity duration-200 pointer-events-none">
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-950/50 backdrop-blur-sm text-background-50">
                          <i className="ri-zoom-in-line text-2xl" />
                        </div>
                      </div>
                      <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-background-950/70 backdrop-blur-sm text-background-50 text-xs font-medium">
                        {activeImage + 1} / {images.length}
                      </div>
                      {hasMultipleImages && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); goPrev(); }}
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/90 hover:bg-background-50 text-foreground-950 backdrop-blur-sm transition-all cursor-pointer"
                            aria-label="Previous image"
                          >
                            <i className="ri-arrow-left-s-line text-xl" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); goNext(); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/90 hover:bg-background-50 text-foreground-950 backdrop-blur-sm transition-all cursor-pointer"
                            aria-label="Next image"
                          >
                            <i className="ri-arrow-right-s-line text-xl" />
                          </button>
                        </>
                      )}
                    </div>
                    {hasMultipleImages && (
                      <div className="flex items-center gap-2 px-4 py-3 overflow-x-auto bg-white border-t border-background-200/60">
                        {images.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => setActiveImage(i)}
                            className={`relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                              i === activeImage
                                ? 'border-primary-500 ring-2 ring-primary-500/30'
                                : 'border-transparent hover:border-background-300/60 opacity-70 hover:opacity-100'
                            }`}
                          >
                            <ImageWithFallback
                              src={img}
                              alt={`Thumbnail ${i + 1}`}
                              className="w-full h-full object-cover object-top"
                              fallbackClassName="w-full h-full"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="h-[240px] rounded-2xl bg-background-100 border border-background-200/60 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-200/50 mx-auto mb-3">
                        <i className="ri-image-line text-2xl text-foreground-400" />
                      </div>
                      <p className="text-sm text-foreground-500">No images available</p>
                    </div>
                  </div>
                )}

                {/* Title + Price */}
                <div className="bg-white border border-background-200/60 rounded-2xl p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div className="flex-1 min-w-0">
                      <h1 className="text-2xl font-display font-bold text-foreground-950 mb-2 leading-snug">
                        {listing.title}
                      </h1>
                      <p className="text-foreground-500 flex items-center gap-2 text-sm">
                        <i className="ri-map-pin-line text-primary-500" />
                        {listing.location}
                        {listing.city && <span className="text-foreground-700 font-medium">, {listing.city}</span>}
                      </p>
                    </div>
                    {showPrices && listing.expected_rent > 0 && (
                      <div className="text-right shrink-0">
                        <div className="text-3xl font-display font-bold text-foreground-950">
                          {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(listing.expected_rent)}
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-1">
                          <span className="text-sm text-foreground-400">/{listing.rent_period || 'mo'}</span>
                          <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 border border-accent-200">Negotiable</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {listing.description && (
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {listing.description}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-background-50 rounded-xl p-4 border border-background-200/60">
                    <div className="flex items-center gap-2 text-foreground-400 text-xs mb-1">
                      <i className="ri-layout-line" />
                      <span>Size</span>
                    </div>
                    <div className="font-display font-bold text-foreground-950">{formatSize(listing.size_acres)}</div>
                  </div>
                  <div className="bg-background-50 rounded-xl p-4 border border-background-200/60">
                    <div className="flex items-center gap-2 text-foreground-400 text-xs mb-1">
                      <i className="ri-building-2-line" />
                      <span>Category</span>
                    </div>
                    <div className="font-display font-bold text-foreground-950">{CATEGORY_LABELS[listing.category] || listing.category}</div>
                  </div>
                  <div className="bg-background-50 rounded-xl p-4 border border-background-200/60">
                    <div className="flex items-center gap-2 text-foreground-400 text-xs mb-1">
                      <i className="ri-eye-line" />
                      <span>Views</span>
                    </div>
                    <div className="font-display font-bold text-foreground-950">{listing.view_count || 0}</div>
                  </div>
                  <div className="bg-background-50 rounded-xl p-4 border border-background-200/60">
                    <div className="flex items-center gap-2 text-foreground-400 text-xs mb-1">
                      <i className="ri-calendar-line" />
                      <span>Posted</span>
                    </div>
                    <div className="font-display font-bold text-foreground-950 text-sm">{formatDate(listing.posted_date)}</div>
                  </div>
                </div>

                {/* Use Cases */}
                {listing.use_cases && listing.use_cases.length > 0 && (
                  <div className="bg-white border border-background-200/60 rounded-2xl p-6">
                    <h3 className="font-display font-semibold text-base text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-checkbox-multiple-line text-primary-500" />
                      Suitable For
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {listing.use_cases.map((uc) => (
                        <span key={uc} className="inline-block px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500/8 text-primary-700 border border-primary-700/20">
                          {uc}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                {listing.features && listing.features.length > 0 && (
                  <div className="bg-white border border-background-200/60 rounded-2xl p-6">
                    <h3 className="font-display font-semibold text-base text-foreground-950 mb-3 flex items-center gap-2">
                      <i className="ri-information-line text-primary-500" />
                      Property Details
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {listing.features.map((f) => (
                        <div key={f} className="bg-background-50 rounded-xl p-3 flex items-center gap-2 text-sm text-foreground-600 border border-background-200/50">
                          <i className="ri-check-line text-teal-400 shrink-0" />
                          {f}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location */}
                <div className="bg-white border border-background-200/60 rounded-2xl p-6">
                  <h3 className="font-display font-semibold text-base text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-map-2-line text-primary-500" />
                    Location
                  </h3>
                  {listing.latitude && listing.longitude ? (
                    <div className="rounded-xl overflow-hidden border border-background-200/60">
                      <iframe
                        title="Property Location"
                        width="100%"
                        height="280"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDummyKey&q=${listing.latitude},${listing.longitude}&zoom=15`}
                      />
                    </div>
                  ) : (
                    <div className="h-28 bg-background-50 border border-background-200/60 rounded-xl flex items-center gap-4 px-6">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-500/10 rounded-xl border border-primary-500/20 shrink-0">
                        <i className="ri-map-pin-line text-primary-500 text-xl" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground-950 text-sm">
                          {listing.location}
                          {listing.city && <span>, {listing.city}</span>}
                        </p>
                        <p className="text-xs text-foreground-400 mt-0.5">Precise map location not available for this property</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Send Enquiry (inline, left column, bottom) */}
                <div className="bg-white border border-background-200/60 rounded-2xl p-6">
                  <div className="mb-5">
                    <h3 className="font-display font-bold text-lg text-foreground-950 flex items-center gap-2">
                      <i className="ri-mail-send-line text-primary-500" />
                      Send Enquiry
                    </h3>
                    <p className="text-sm text-foreground-500 mt-1">
                      Interested in this property? Fill in your details and we&apos;ll get back to you.
                    </p>
                  </div>
                  <EnquiryForm listing={listing} />
                </div>
              </div>

              {/* Right: Sidebar */}
              <div className="space-y-5">

                {/* Property Owner Card */}
                <div className="bg-white border border-background-200/60 rounded-2xl p-6">
                  <h3 className="font-display font-semibold text-base text-foreground-950 mb-4">Property Owner</h3>
                  {showOwnerInfo && listing.owner_name ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 shrink-0">
                          <i className="ri-user-line text-xl text-primary-500" />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground-950">{listing.owner_name}</p>
                          <p className="text-xs text-foreground-400">Property Owner</p>
                        </div>
                      </div>
                      {(listing as unknown as Record<string, unknown>).owner_contact && (
                        <a
                          href={`tel:${(listing as unknown as Record<string, unknown>).owner_contact as string}`}
                          className="flex items-center gap-2 w-full py-2.5 rounded-xl bg-background-100 hover:bg-background-200/50 border border-background-200/60 text-sm font-medium text-foreground-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-phone-line text-primary-500 ml-3" />
                          {(listing as unknown as Record<string, unknown>).owner_contact as string}
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="bg-background-50 rounded-xl p-5 text-center border border-background-200/50">
                      <div className="w-12 h-12 flex items-center justify-center bg-background-100 rounded-full mx-auto mb-3">
                        <i className="ri-shield-user-line text-xl text-foreground-400" />
                      </div>
                      <p className="text-sm font-semibold text-foreground-700 mb-1">Owner information is hidden</p>
                      <p className="text-xs text-foreground-400">Contact details are kept private. Submit an enquiry to connect.</p>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-gradient-to-br from-accent-50 to-primary-50 border border-accent-100 rounded-2xl p-6">
                  <h3 className="font-display font-bold text-base text-foreground-950 mb-4">Interested?</h3>
                  <p className="text-sm text-foreground-600 mb-4">
                    Get in touch with our agents for site visits and negotiations.
                  </p>
                  <div className="space-y-3">
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex flex-col items-center justify-center gap-0.5 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white transition-colors cursor-pointer"
                    >
                      <span className="flex items-center gap-2 font-semibold text-sm whitespace-nowrap">
                        <i className="ri-whatsapp-line text-lg" />
                        WhatsApp Enquiry
                      </span>
                      <span className="text-xs text-white/85 whitespace-nowrap">{whatsappDisplay}</span>
                    </a>
                    <Link
                      to="/feasibility-report"
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-primary-700/40 text-primary-600 font-semibold text-sm hover:bg-primary-500/10 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-file-chart-line" />
                      Get Feasibility Report
                    </Link>
                  </div>
                </div>

                {/* Similar Listings CTA */}
                <div className="bg-white border border-background-200/60 rounded-2xl p-6">
                  <h3 className="font-display font-semibold text-base text-foreground-950 mb-3">Looking for more?</h3>
                  <p className="text-sm text-foreground-500 mb-4">
                    Browse similar properties{listing.city && ` in ${listing.city}`}.
                  </p>
                  <Link
                    to={`/listings${listing.city ? `?city=${encodeURIComponent(listing.city)}` : ''}`}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-background-100 hover:bg-background-200/60 border border-background-200/60 text-foreground-700 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-search-line" />
                    View Similar Listings
                  </Link>
                </div>

                {/* Metadata */}
                <div className="bg-white border border-background-200/60 rounded-2xl p-5 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-500 flex items-center gap-1.5"><i className="ri-eye-line" /> Views</span>
                    <span className="text-foreground-950 font-semibold">{listing.view_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-500 flex items-center gap-1.5"><i className="ri-calendar-line" /> Posted</span>
                    <span className="text-foreground-950 font-medium">{formatDate(listing.posted_date)}</span>
                  </div>
                  {listing.transaction_types && listing.transaction_types.length > 0 && (
                    <div className="pt-2 border-t border-background-200/50">
                      <p className="text-xs text-foreground-500 mb-2">Available for</p>
                      <div className="flex flex-wrap gap-1.5">
                        {listing.transaction_types.map((t) => (
                          <span key={t} className="text-xs px-2 py-1 rounded-lg bg-secondary-100 text-secondary-700 border border-secondary-200 capitalize">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Share */}
                <div className="bg-white border border-background-200/60 rounded-2xl p-5">
                  <p className="text-xs text-foreground-500 mb-3 font-medium">Share this listing</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigator.clipboard.writeText(window.location.href)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer"
                      title="Copy link"
                    >
                      <i className="ri-link" />
                    </button>
                    <button
                      onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${listing.title} — ${window.location.href}`)}`, '_blank')}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer"
                      title="Share on WhatsApp"
                    >
                      <i className="ri-whatsapp-line" />
                    </button>
                    <button
                      onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(listing.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer"
                      title="Share on X"
                    >
                      <i className="ri-twitter-x-line" />
                    </button>
                  </div>
                </div>

                <Link to="/listings" className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors px-1">
                  <i className="ri-arrow-left-line" /> Back to all listings
                </Link>
              </div>
            </div>
          </section>

          <SimilarListings
            currentId={listing.id}
            category={listing.category}
            location={listing.location}
            city={listing.city}
          />

          {lightboxOpen && (
            <Lightbox
              images={images}
              initialIndex={activeImage}
              alt={listing.title}
              onClose={() => setLightboxOpen(false)}
              onPrev={goPrev}
              onNext={goNext}
            />
          )}
        </>
      )}
    </PageLayout>
  );
}